// Coletor de erros para telemetria de UX.
// - window.onerror para erros JS não capturados.
// - window.onunhandledrejection para promessas rejeitadas sem catch.
// - Wrapper opcional sobre window.fetch que registra APENAS falhas (rede ou status >=500).
//
// Importante: o ErrorBoundary em src/App.js continua chamando logUxEvent('error_boundary',...)
// diretamente — este módulo é complementar, não substitui o ErrorBoundary.

import { logUxEvent } from './uxLogger';
import { recordFailure as netRecordFailure, recordSuccess as netRecordSuccess } from './networkHealth';

const TRUNC_MSG = 200;
const TRUNC_STACK = 600;

const trunc = (s, n) => (s == null ? null : String(s).slice(0, n));

// Tracking pixels do Google Ads / Analytics 4 (rmkt/collect, ccm/collect,
// ccm/form-data, etc.) sao bloqueados por adblockers. Cada paciente com
// uBlock/Brave gera ~10 fetch_errors apenas desses dominios. Auditoria
// 13/05 mostrou 10.000+ ocorrencias/dia — puro ruido. Esses requests sao
// disparados pelo proprio gtag.js da Google e nao temos controle sobre
// eles; mesmo se falharem, nao afetam UX. Filtramos para limpar o sinal.
//
// 18/05: ampliado para cobrir googleads.g.doubleclick.net, google-analytics.com,
// stats.g.doubleclick.net, googletagmanager, fonts.googleapis (preconnect blocked),
// connect.facebook.net e bat.bing.com (todos comuns em adblockers).
const ADBLOCK_TRACKING_RE =
  /^https?:\/\/(?:[\w-]+\.)*(?:google\.com\/(?:rmkt\/collect|ccm\/(?:collect|form-data)|pagead)|googleads\.g\.doubleclick\.net|stats\.g\.doubleclick\.net|google-analytics\.com|googletagmanager\.com|connect\.facebook\.net|bat\.bing\.com|adservice\.google\.com|analytics\.google\.com)/i;
const isAdblockedTracking = (url) => {
  if (!url) return false;
  return ADBLOCK_TRACKING_RE.test(url);
};

// 18/05: o painel medico (/medico) faz polling de /payments/status para cada
// paciente da fila. Quando o backend treme por 30s, viram dezenas de fetch_error
// que nao representam degradacao real do app — o polling e best-effort e o
// medico nao percebe a falha. Auditoria 16/05 mostrou 91 ocorrencias num unico
// dia para esse caso. Filtramos para limpar o sinal sem perder erros do
// fluxo do paciente em /payments/status (que ainda sao logados).
const isDoctorPanelPaymentPoll = (url, route) => {
  if (!url || route !== '/medico') return false;
  return /\/payments\/status\b/i.test(url);
};

// Endpoints de polling best-effort do fluxo do paciente: uma falha isolada de
// rede se auto-recupera no ciclo seguinte (5-60s) e não representa degradação
// real. Auditoria 09/08/2026 (UX-002): 62 eventos/38 sessões de blips em
// /payments/status sem impacto no fluxo (o polling seguiu e confirmou).
// Só logamos fetch_error de network_failure a partir da 3ª falha CONSECUTIVA
// no mesmo endpoint — qualquer resposta (mesmo 5xx, que já tem log próprio)
// zera o contador. Auditoria 25/08: /payments/status + queue-status +
// self-refund + /token geravam ruído de blip (181 sessões) sem impacto no fluxo.
const POLLING_PATH_RE =
  /\/(?:payments\/(?:status|queue-status|self-refund)|token)\b|\/doctor\/availability|\/conduct-drafts\/pending/i;
const pollFailStreak = new Map();
const pollPathKey = (url) => {
  try { return new URL(url).pathname; } catch { return String(url || ''); }
};

// Cancelamentos legítimos (AbortController do Stream Chat, polling cancelado em
// unmount, navegação interrompendo fetch, etc.) não são erros do app — são
// ruído operacional. Filtrar evita inflar o sinal de fetch_error.
const isAbortError = (err, init) => {
  try {
    if (init?.signal?.aborted) return true;
  } catch {}
  if (!err) return false;
  if (err.name === 'AbortError' || err.code === 20 /* DOMException ABORT_ERR */) return true;
  const msg = String(err.message || err || '');
  return /signal is aborted|aborted without reason|the operation was aborted|abort/i.test(msg);
};

// Pagamento.jsx + ConsultaChat.jsx (paywall) fazem polling de /payments/status
// com AbortController no unmount/showPaywall change. iOS costuma reportar
// "Failed to fetch" em vez de AbortError. ConsultaChat passou a usar timeout
// client-side de 15s (auditoria 21/05: 117 erros, P50=55s, max=87min). Aborts
// continuam validos quando o paciente fecha paywall/aba antes de 15s.
const PAYMENT_POLL_ROUTES = new Set(['/pagamento', '/consulta/chat', '/consulta/chat/pago']);
const isPaymentStatusPollCancelled = (url, route, err, init) => {
  if (!PAYMENT_POLL_ROUTES.has(route) || !/\/payments\/status\b/i.test(url || '')) return false;
  return isAbortError(err, init);
};

// Reconexões do Stream Chat (WebSocket caindo e voltando, presence em
// background, getstream.io/stream-io-api.com edge swap) frequentemente vazam
// como "Failed to fetch" / "Load failed" sem AbortError. Telemetria de 04/05
// mostrou ~25k destes em 21h — quase todos eram reconexão automática que o
// próprio cliente já trata. Filtrar limpa o sinal sem perder erros reais.
const STREAM_CHAT_HOST_RE = /(?:^https?:\/\/|^wss?:\/\/)?(?:[\w-]+\.)?(?:getstream\.io|stream-io-api\.com|streamio\b)/i;
// Paths que o Stream Chat usa internamente para fallback longpoll/connect
// quando WebSocket não funciona — independente do host (algumas integrações
// usam proxy reverso via apiV2).
const STREAM_CHAT_PATH_RE = /\/(?:connect|longpoll|presence|channels?\/messaging\/[^/?]+\/(?:query|read|send|stop-watching))/i;
const TRANSIENT_NETWORK_MSG_RE = /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?|Fetch is aborted|The network connection was lost\.?|cancelled)$/i;
const isStreamChatTransient = (url, errMsg) => {
  if (!url) return false;
  const isStream = STREAM_CHAT_HOST_RE.test(url) || STREAM_CHAT_PATH_RE.test(url);
  if (!isStream) return false;
  return TRANSIENT_NETWORK_MSG_RE.test(String(errMsg || '').trim());
};

// Página em background frequentemente derruba fetches em curso. Não é erro
// real do app — é o browser se protegendo. visibilityState pode ser 'hidden'
// ou 'unloaded' nesse momento. Quando o paciente voltar pra tela, a query
// vai ser reemitida.
const isBackgroundFetch = () => {
  try {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  } catch {
    return false;
  }
};

// Detecta a assinatura clássica de "build velho com chunk hash novo" — cliente
// pediu /static/js/123.abc.chunk.js, Firebase rewrite respondeu com index.html,
// o JS engine quebra com "Unexpected token '<'" porque está parseando HTML.
// Auditoria 11/05 mostrou 40+ ocorrências em /consulta/chat.
//
// O fix: forçar reload com cache-buster para furar CDN/SW. Usamos sessionStorage
// como flag anti-loop (se reload não resolver, evita reboot infinito).
const STALE_CHUNK_RE = /Unexpected token ['"]?<['"]?|Loading(?: CSS)? chunk \d+ failed|Failed to fetch dynamically imported module|ChunkLoadError/i;
const STREAM_DISCONNECT_RE = /can'?t use a channel after client\.disconnect/i;
const RELOAD_FLAG = 'stale_chunk_reload_at';
const tryReloadOnStaleChunk = (rawMsg) => {
  try {
    const msg = String(rawMsg || '');
    const isChunk = STALE_CHUNK_RE.test(msg);
    const isDisconnect = STREAM_DISCONNECT_RE.test(msg);
    if (!isChunk && !isDisconnect) return false;
    if (typeof window === 'undefined' || !window.location) return false;
    if (window.__FORCE_UPDATE_IN_PROGRESS__) return false;
    // Anti-loop: se já tentamos reload nos últimos 60s, não reboota de novo.
    let last = 0;
    try { last = parseInt(sessionStorage.getItem(RELOAD_FLAG) || '0', 10) || 0; } catch {}
    const now = Date.now();
    if (now - last < 60_000) return false;
    try { sessionStorage.setItem(RELOAD_FLAG, String(now)); } catch {}
    try {
      logUxEvent(isDisconnect ? 'stream_disconnect_recover' : 'stale_chunk_recover', {
        route: window.location.pathname,
        msg: msg.slice(0, 120),
      });
    } catch {}
    // Limpa caches/SW e recarrega com cache-buster
    if ('caches' in window) {
      try { caches.keys().then((names) => names.forEach((n) => caches.delete(n))); } catch {}
    }
    if ('serviceWorker' in navigator) {
      try { navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.unregister())); } catch {}
    }
    const url = new URL(window.location.href);
    url.searchParams.set('cb', String(now));
    setTimeout(() => { try { window.location.replace(url.toString()); } catch { try { window.location.reload(); } catch {} } }, 50);
    return true;
  } catch { return false; }
};

// Script que o Outlook SafeLinks / Edge WebView injeta na página (invokeOnClient)
// rejeita promessas em loop quando o elemento alvo dele some do DOM. Auditoria
// 11/08/2026: ~45k eventos "Object Not Found Matching Id:N, MethodName:update,
// ParamCount:4" em 14 dias, vindos de um punhado de sessões Edge/Outlook.
// Não é código nosso e não afeta o paciente — filtramos na origem.
const THIRD_PARTY_REJECTION_RE = /Object Not Found Matching Id:\d+/i;

// Cap por assinatura: uma rejection em loop (ex.: dentro de setInterval) gerava
// milhares de writes numa única sessão. As primeiras ocorrências carregam todo
// o valor diagnóstico; a última logada vai marcada com capped:true para o
// aggregator saber que houve supressão.
const REJECTION_CAP_PER_SIGNATURE = 10;
const rejectionCounts = new Map();

// "Script error." opaco (cross-origin sem stack) não tem o que diagnosticar —
// 14.8k eventos/14d. Mantemos 3 por página carregada só para sinal de presença.
const OPAQUE_JS_ERROR_CAP = 3;
let opaqueJsErrorCount = 0;

const onJsError = (event) => {
  try {
    const err = event && event.error;
    const message = event?.message || (err && err.message) || 'unknown';
    // Tenta auto-recuperação ANTES de logar, para não inflar o sinal.
    if (tryReloadOnStaleChunk(message)) return;
    // "Script error." sem filename/stack é erro de script cross-origin sem
    // CORS (pixels de ads: fbevents.js, bat.bing.com etc. — verificado
    // 19/07/2026 que esses CDNs não servem ACAO, então crossorigin não é
    // opção). O browser esconde tudo; não há o que diagnosticar. Marcamos
    // como opaque para o aggregator/dashboards filtrarem o ruído sem perder
    // erros reais do bundle (que são same-origin e vêm com stack).
    const opaque = /^Script error\.?$/i.test(String(message).trim())
      && !event?.filename && !(err && err.stack);
    if (opaque) {
      opaqueJsErrorCount += 1;
      if (opaqueJsErrorCount > OPAQUE_JS_ERROR_CAP) return;
    }
    logUxEvent('js_error', {
      error_message: trunc(message, TRUNC_MSG),
      error_name: err?.name || 'Error',
      error_stack: trunc(err?.stack, TRUNC_STACK),
      error_filename: trunc(event?.filename, TRUNC_MSG),
      error_lineno: event?.lineno || null,
      error_colno: event?.colno || null,
      opaque_third_party: opaque,
      route: window.location.pathname,
    });
  } catch {}
};

const onUnhandledRejection = (event) => {
  try {
    const reason = event && event.reason;
    if (isAbortError(reason)) return; // ruído de cancelamento, não erro real
    const message = (reason && (reason.message || String(reason))) || 'unknown';
    // Auto-recover de stale chunks que vem como Promise rejection (lazy import).
    if (tryReloadOnStaleChunk(message)) return;
    if (THIRD_PARTY_REJECTION_RE.test(message)) return; // Outlook/Edge WebView, não é nosso
    const signature = String(message).slice(0, 120);
    const count = (rejectionCounts.get(signature) || 0) + 1;
    rejectionCounts.set(signature, count);
    if (count > REJECTION_CAP_PER_SIGNATURE) return;
    logUxEvent('unhandled_rejection', {
      error_message: trunc(message, TRUNC_MSG),
      error_name: reason?.name || 'PromiseRejection',
      error_stack: trunc(reason?.stack, TRUNC_STACK),
      route: window.location.pathname,
      seq: count,
      capped: count === REJECTION_CAP_PER_SIGNATURE,
    });
  } catch {}
};

let fetchPatched = false;
const patchFetch = () => {
  if (fetchPatched || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  fetchPatched = true;
  const orig = window.fetch.bind(window);
  window.fetch = async function patchedFetch(input, init) {
    const start = Date.now();
    let url = '';
    let method = 'GET';
    try {
      url = typeof input === 'string' ? input : (input && input.url) || '';
      method = String((init && init.method) || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
    } catch {}

    // Normaliza URL relativa para absoluta. Sem isso, o aggregator extrai
    // host de uma URL relativa usando uma base placeholder (placeholder.local)
    // e perdemos visibilidade do destino real. Com a URL absoluta, o host fica
    // consultamaissimplificada.web.app, us-central1-...cloudfunctions.net etc.
    try {
      if (url && !/^[a-z][a-z0-9+.-]*:/i.test(url)) {
        url = new URL(url, window.location.origin).toString();
      }
    } catch {}

    // Não loga próprios eventos para evitar loop infinito.
    const isTelemetry = /\/ux\/ingest($|\?)/i.test(url);

    try {
      const res = await orig(input, init);
      const route = window.location.pathname;
      if (
        !isTelemetry &&
        !isAdblockedTracking(url) &&
        !isDoctorPanelPaymentPoll(url, route) &&
        res && res.status >= 500
      ) {
        logUxEvent('fetch_error', {
          url: trunc(url, TRUNC_MSG),
          method,
          status: res.status,
          duration_ms: Date.now() - start,
          kind: 'http_5xx',
          route,
        });
      }
      // Sucesso (ou erro do app tipo 4xx) conta como sinal de que a rede está OK
      // para o domínio. Limpa o contador de degradação. 5xx também sai pois
      // significa que conseguimos chegar no servidor — é problema do backend,
      // não da rede do paciente.
      if (!isTelemetry && res) {
        try { netRecordSuccess(url); } catch {}
        if (POLLING_PATH_RE.test(url)) pollFailStreak.delete(pollPathKey(url));
      }
      return res;
    } catch (err) {
      const route = window.location.pathname;
      const skip =
        isTelemetry ||
        isAbortError(err, init) ||
        isPaymentStatusPollCancelled(url, route, err, init) ||
        isStreamChatTransient(url, err?.message) ||
        isAdblockedTracking(url) ||
        isDoctorPanelPaymentPoll(url, route) ||
        // Falhas durante backgrounding são esperadas (browser cancela fetches
        // em curso quando aba/app vai pra background) — só descartamos para
        // erros transitórios genéricos, não pra casos onde a mensagem indica
        // outra coisa (ex.: 5xx, CORS).
        (isBackgroundFetch() && TRANSIENT_NETWORK_MSG_RE.test(String(err?.message || '').trim()));
      if (!skip) {
        // Alimenta o detector de degradação para a UI poder reagir (tem
        // thresholds próprios — roda mesmo quando o log é suprimido abaixo).
        try { netRecordFailure(url); } catch {}
        let consecutive = 1;
        let suppress = false;
        if (POLLING_PATH_RE.test(url)) {
          const key = pollPathKey(url);
          consecutive = (pollFailStreak.get(key) || 0) + 1;
          pollFailStreak.set(key, consecutive);
          suppress = consecutive < 3;
        }
        if (!suppress) {
          logUxEvent('fetch_error', {
            url: trunc(url, TRUNC_MSG),
            method,
            status: 0,
            duration_ms: Date.now() - start,
            kind: 'network_failure',
            error_name: err?.name || 'Error',
            error_message: trunc(err && err.message, TRUNC_MSG),
            consecutive,
            route,
          });
        }
      }
      throw err;
    }
  };
};

let initialized = false;
export const initErrors = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  window.addEventListener('error', onJsError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  patchFetch();
};
